import { useState, useEffect } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';

import Util from '../util/util';
import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import ToggleSwitch from './ToggleSwitch';
import { useTranslation } from '../i18n';

/**
 * NotificationSubscriptionToggles - Toggles for managing email notification preferences
 * Supports multiple organizations with independent preferences per org
 *
 * @param {string[]} organizations - Array of organization names (or single string for backwards compat)
 * @param {string} userEmail - User's email (for admin mode, otherwise uses logged-in user)
 * @param {boolean} isAdmin - Whether this is admin mode (managing another user)
 * @param {function} onSubscriptionChange - Optional callback when subscription changes
 */
const NotificationSubscriptionToggles = ({
  organizations,
  organization, // backwards compat - single org
  userEmail,
  isAdmin = false,
  onSubscriptionChange,
}) => {
  const { t } = useTranslation('reports');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  // subscriptions is a map: { orgName: { detail: bool, summary: bool } }
  const [subscriptions, setSubscriptions] = useState({});

  // Normalize to array of orgs
  const orgList = organizations || (organization ? [organization] : []);

  useEffect(() => {
    // Only fetch for orgs we don't already have data for
    const missingOrgs = orgList.filter((org) => !subscriptions[org]);
    if (missingOrgs.length > 0) {
      fetchSubscriptionsForOrgs(missingOrgs);
    }
    // Clean up orgs that are no longer in the list
    const currentOrgSet = new Set(orgList);
    const subsToRemove = Object.keys(subscriptions).filter((org) => !currentOrgSet.has(org));
    if (subsToRemove.length > 0) {
      setSubscriptions((prev) => {
        const updated = { ...prev };
        subsToRemove.forEach((org) => delete updated[org]);
        return updated;
      });
    }
  }, [JSON.stringify(orgList), userEmail]);

  const fetchSubscriptionsForOrgs = async (orgsToFetch) => {
    if (!orgsToFetch.length) return;

    // Only show full loading state on initial load
    const isInitialLoad = Object.keys(subscriptions).length === 0;
    if (isInitialLoad) {
      setLoading(true);
    }

    try {
      const endpoint = isAdmin ? '/users/admin/subscription' : '/users/subscription';

      // Fetch subscriptions for specified orgs in parallel
      const promises = orgsToFetch.map(async (org) => {
        const queryParams = isAdmin
          ? { organization: org, user: userEmail }
          : { organization: org };

        try {
          const result = await Util.get('UserManagement', endpoint, { queryParams });
          if (result.subscription) {
            return {
              org,
              detail: result.subscription.detail !== false,
              summary: result.subscription.summary === true,
            };
          }
        } catch (error) {
          console.log(`Error fetching subscription for ${org}:`, error);
        }
        return { org, detail: true, summary: false };
      });

      const results = await Promise.all(promises);

      // Merge new results with existing subscriptions
      setSubscriptions((prev) => {
        const updated = { ...prev };
        results.forEach(({ org, detail, summary }) => {
          updated[org] = { detail, summary };
        });
        return updated;
      });
    } catch (error) {
      console.log('Error fetching subscriptions:', error);
    }
    if (isInitialLoad) {
      setLoading(false);
    }
  };

  const updateSubscription = async (org, newDetail, newSummary) => {
    setSaving((prev) => ({ ...prev, [org]: true }));
    try {
      const endpoint = isAdmin ? '/users/admin/subscription' : '/users/subscription';
      const body = isAdmin
        ? { organization: org, user: userEmail, detail: newDetail, summary: newSummary }
        : { organization: org, detail: newDetail, summary: newSummary };

      await Util.post('UserManagement', endpoint, { body });

      setSubscriptions((prev) => ({
        ...prev,
        [org]: { detail: newDetail, summary: newSummary },
      }));

      if (onSubscriptionChange) {
        onSubscriptionChange({ organization: org, detail: newDetail, summary: newSummary });
      }
    } catch (error) {
      console.log('Error updating subscription:', error);
      // Revert on error - refetch this org
      fetchSubscriptionsForOrgs([org]);
    }
    setSaving((prev) => ({ ...prev, [org]: false }));
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
      </View>
    );
  }

  // Single org - compact view (no org name header)
  if (orgList.length === 1) {
    const org = orgList[0];
    const sub = subscriptions[org] || { detail: true, summary: false };
    const isSaving = saving[org];

    return (
      <View style={styles.container}>
        <View style={styles.toggleRow}>
          <View style={styles.toggleInfo}>
            <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
              {t('notifications.reportNotifications')}
            </CustomText>
            <CustomText size={12} color={AppStyles.colorSet.textSecondary}>
              {t('notifications.reportNotificationsHint')}
            </CustomText>
          </View>
          <ToggleSwitch
            value={sub.detail}
            onValueChange={(value) => updateSubscription(org, value, sub.summary)}
            disabled={isSaving}
          />
        </View>

        <View style={styles.toggleRow}>
          <View style={styles.toggleInfo}>
            <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
              {t('notifications.weeklySummary')}
            </CustomText>
            <CustomText size={12} color={AppStyles.colorSet.textSecondary}>
              {t('notifications.weeklySummaryHint')}
            </CustomText>
          </View>
          <ToggleSwitch
            value={sub.summary}
            onValueChange={(value) => updateSubscription(org, sub.detail, value)}
            disabled={isSaving}
          />
        </View>
      </View>
    );
  }

  // Multiple orgs - use 2-column grid for compactness
  return (
    <View style={styles.container}>
      <View style={styles.gridContainer}>
        {orgList.map((org) => {
          const sub = subscriptions[org] || { detail: true, summary: false };
          const isSaving = saving[org];

          return (
            <View key={org} style={styles.gridItem}>
              <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textPrimary} numberOfLines={1}>
                {org}
              </CustomText>
              <View style={styles.toggleRowInline}>
                <View style={styles.toggleInlineItem}>
                  <CustomText size={10} color={AppStyles.colorSet.textSecondary}>
                    {t('notifications.reports')}
                  </CustomText>
                  <ToggleSwitch
                    value={sub.detail}
                    onValueChange={(value) => updateSubscription(org, value, sub.summary)}
                    disabled={isSaving}
                    small
                  />
                </View>
                <View style={styles.toggleInlineItem}>
                  <CustomText size={10} color={AppStyles.colorSet.textSecondary}>
                    {t('notifications.summary')}
                  </CustomText>
                  <ToggleSwitch
                    value={sub.summary}
                    onValueChange={(value) => updateSubscription(org, sub.detail, value)}
                    disabled={isSaving}
                    small
                  />
                </View>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 4,
  },
  loadingContainer: {
    padding: 12,
    alignItems: 'center',
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 2,
  },
  toggleInfo: {
    flex: 1,
    marginRight: 12,
  },
  // Grid layout for multiple orgs
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  gridItem: {
    backgroundColor: AppStyles.colorSet.backgroundSecondary || '#F9FAFB',
    borderRadius: 6,
    padding: 8,
    minWidth: 140,
    flex: 1,
    maxWidth: '48%',
  },
  toggleRowInline: {
    flexDirection: 'column',
    gap: 2,
    marginTop: 4,
  },
  toggleInlineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

export default NotificationSubscriptionToggles;
